const parseJsonAttribute = (element, attributeName) => {
  const value = element.getAttribute(attributeName);
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.error(`Failed to parse attribute ${attributeName}`, error);
    return null;
  }
};

class GiftButton extends HTMLElement {
  constructor() {
    super();
    this.hasBeenPositioned = false;
    this.appEnvironment = this.getAttribute("data-app-environment");
    this.container = document.getElementById("ribbon-button-container");
    this.isDesignMode = Shopify?.designMode || false;
    this.originalWidth = this.container?.style.width || null;
    this.giftButton = this.querySelector("button");
    this.hasMerchantStyles = this.checkForMerchantStyles();
    this.merchantConfig =
      parseJsonAttribute(this, "data-merchant-config") || {};
    this.product = parseJsonAttribute(this, "data-product") || {};
    this.statsigData = parseJsonAttribute(this, "data-statsig");
    this.customerData = parseJsonAttribute(this, "data-customer");
    this.variantMetadataMap =
      parseJsonAttribute(this, "data-variant-gift-metadata") || {};
    this.shopId = this.getAttribute("data-shop-id") || null;
    this.shopDomain =
      this.getAttribute("data-shop-domain") || this.statsigData?.shopDomain;
    if (!this.hasMerchantStyles) {
      // Initialize ResizeObserver to watch for size changes
      this.resizeObserver = new ResizeObserver((entries) => {
        // Log an error if we somehow get multiple entries
        if (entries.length > 1) {
          console.error(
            `ResizeObserver received ${entries.length} entries when expecting only 1. ` +
            "This may indicate a bug where multiple elements are being observed.",
            entries,
          );
        }
        // We only care about the first entry since we're only observing one element
        const entry = entries[0];
        const newMaxWidth = entry.borderBoxSize[0].inlineSize;
        if (newMaxWidth > 0) {
          this.updateButtonMaxWidth(`${newMaxWidth}px`);
        }
      });

      this.matchAddToCartWidth();
      const addToCartButton = this.findAddToCartButton();
      if (addToCartButton) {
        // Start observing the add to cart button for size changes
        this.resizeObserver.observe(addToCartButton);
      } else {
        console.error("Could not find Add to Cart button");
      }
    }
    this.currentVariantId = null;
    this.variantInputObserver = null;
    this.urlObserver = null;
  }
  findAddToCartButton() {
    // Common Shopify add to cart button selectors
    const addToCartSelectors = [
      '[name="add"]',
      '[data-action="add-to-cart"]',
      ".product-form__submit",
      "#AddToCart",
      ".add-to-cart",
    ];

    // Find the add to cart button
    return addToCartSelectors.reduce((found, selector) => {
      return found || document.querySelector(selector);
    }, null);
  }
  checkForMerchantStyles() {
    if (!this.giftButton) {
      return false;
    }

    // Check if merchant added custom classes via schema settings
    const customClasses = (
      this.giftButton.getAttribute("class")?.split(" ") || []
    )
      .filter((className) => className.trim())
      .filter(
        (className) =>
          className !== "button" &&
          className !== "btn" &&
          className !== "ribbon-custom-button",
      );
    const hasCustomClasses = customClasses.length > 0;
    return hasCustomClasses;
  }
  updateButtonMaxWidth(maxWidth) {
    if (this.container && maxWidth) {
      this.container.style.maxWidth = maxWidth;

      if (this.giftButton) {
        this.giftButton.style.maxWidth = maxWidth;
        this.giftButton.style.width = "100%";
      }
    }
  }

  matchAddToCartWidth() {
    if (this.hasMerchantStyles) {
      return;
    }

    const addToCartButton = this.findAddToCartButton();
    if (addToCartButton) {
      // Get initial width from computed style
      const computedStyle = window.getComputedStyle(addToCartButton);
      const maxWidth = computedStyle.getPropertyValue("width");
      if (maxWidth && maxWidth !== "0px") {
        // Apply the width of the Add to cart button
        this.updateButtonMaxWidth(maxWidth);
      }
    }
  }

  getCookie = (name) => {
    const value = Cookies.get(name);
    if (value === undefined) {
      return null;
    }
    try {
      return JSON.parse(value);
    } catch (e) {
      return value;
    }
  };

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.urlObserver) {
      this.urlObserver.disconnect();
    }
    if (this.variantInputObserver) {
      this.variantInputObserver.disconnect();
    }
  }

  async connectedCallback() {
    this.initializeGlobalContext();

    const statsigContext = this.statsigData || {};
    const statsigShopDomain = this.shopDomain || statsigContext.shopDomain;

    // Log product page view event
    await initializeStatsig(this.appEnvironment, statsigShopDomain);
    if (window.statsig && window.isStatsigInitialized) {
      window.statsig.logEvent("product_page_viewed", "", {
        product_id: this.product.id,
        variant_id: this.getAttribute("data-initial-variant"),
        customer_orders_count: statsigContext.customerOrdersCount,
        customer_id: statsigContext.customerId,
        shop_domain: statsigShopDomain,
        cart_item_count: statsigContext.cartItemCount,
        cart_subtotal: statsigContext.cartSubtotal,
      });
    } else {
      console.error("Statsig not initialized in liquid");
    }

    this.addEventListener("click", this.toggleModal);
    // Position Button if needed
    if (this.merchantConfig?.customPositionClass && !this.hasBeenPositioned) {
      const className = this.merchantConfig.customPositionClass;
      this.positionBelowAddToCart(className);
    }
    this.setInitialVariantId();
    this.initializeVariantListeners();
    const initialVariantId = this.getCurrentVariantId();

    if (initialVariantId) {
      this.updateVisibility(initialVariantId);
    }
  }

  initializeGlobalContext() {
    if (this.customerData) {
      window.customer = {
        ...(window.customer || {}),
        ...this.customerData,
      };
    } else if (!window.customer) {
      window.customer = {};
    }

    if (this.shopId) {
      window.shopId = this.shopId;
    }

    if (this.shopDomain) {
      window.shopDomain = this.shopDomain;
    } else if (this.statsigData?.shopDomain) {
      window.shopDomain = this.statsigData.shopDomain;
    }

    if (this.statsigData) {
      window.statsigCustomerOrdersCount =
        this.statsigData.customerOrdersCount;
      window.statsigCustomerId = this.statsigData.customerId;
      window.statsigCartItemCount = this.statsigData.cartItemCount;
      window.statsigCartSubtotal = this.statsigData.cartSubtotal;
      window.statsigShopDomain =
        this.shopDomain || this.statsigData.shopDomain;
    }
  }

  positionBelowAddToCart(className) {
    const targetContainer = document.querySelector(className);
    if (targetContainer && this.container) {
      targetContainer.appendChild(this.container);
      this.hasBeenPositioned = true;
    }
  }

  getCurrentVariantId() {
    return this.currentVariantId;
  }

  setInitialVariantId() {
    // Try URL first
    const urlVariantId = this.getVariantIdFromUrl();
    if (urlVariantId) {
      this.updateCurrentVariantId(urlVariantId);
      return;
    }

    // Try input field next
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput?.value) {
      this.updateCurrentVariantId(variantInput.value);
      return;
    }

    // If both fail fallback to liquid attribute id
    const initialVariantId = this.getAttribute("data-initial-variant");
    if (initialVariantId) {
      this.updateCurrentVariantId(initialVariantId);
      return;
    }

    // If no variant ID is found, log it
    console.warn("No initial variant ID found");
  }

  getVariantIdFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("variant");
  }

  updateCurrentVariantId(newVariantId) {
    if (newVariantId !== this.currentVariantId) {
      this.currentVariantId = newVariantId;
      this.updateVisibility(newVariantId);
    }
  }

  initializeVariantListeners() {
    // Method 1: Listen for variant:change event
    document.addEventListener("variant:change", (event) => {
      const variant = event.detail?.variant;
      if (variant?.id) {
        this.updateCurrentVariantId(variant.id);
      }
    });

    // Method 2: Watch for changes to variant input field
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput) {
      this.variantInputObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "value"
          ) {
            const newVariantId = variantInput.value;
            if (newVariantId) {
              this.updateCurrentVariantId(newVariantId);
            }
          }
        });
      });

      this.variantInputObserver.observe(variantInput, {
        attributes: true,
        attributeFilter: ["value"],
      });
    }

    // Method 3: Set up URL monitoring
    let currentUrl = window.location.href;
    this.urlObserver = new MutationObserver(() => {
      if (currentUrl !== window.location.href) {
        currentUrl = window.location.href;
        const urlVariantId = this.getVariantIdFromUrl();
        if (urlVariantId) {
          this.updateCurrentVariantId(urlVariantId);
        }
      }
    });

    this.urlObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  updateButtonState(isAvailable) {
    if (this.giftButton) {
      if (!isAvailable) {
        this.giftButton.style.opacity = "0.5";
        this.giftButton.style.cursor = "not-allowed";
        this.giftButton.style.pointerEvents = "none";
      } else {
        this.giftButton.style.opacity = "1";
        this.giftButton.style.cursor = "pointer";
        this.giftButton.style.pointerEvents = "auto";
      }
    }
  }

  async updateVisibility(variantId) {
    const getBaseUrl = (url) => {
      return url.split("?")[0];
    };
    // Param Logic for showing gift button
    const urlParams = new URLSearchParams(window.location.search);
    const allParams = Object.fromEntries(urlParams);
    // Check for stored cookie state
    const storedBaseUrl = this.getCookie("merchantConfigBaseUrl");
    const currentBaseUrl = getBaseUrl(window.location.href);
    const shouldShowGiftButtonBasedOnUrl =
      !this.merchantConfig?.showGiftThroughUniqueLink ||
      allParams[this.merchantConfig?.showGiftThroughUniqueLink?.paramName] ===
      this.merchantConfig?.showGiftThroughUniqueLink?.paramValue ||
      storedBaseUrl === currentBaseUrl;

    const variantData = this.variantMetadataMap?.[variantId];
    const isAvailable = Boolean(variantData?.available);
    const exclude_product = this.product.tags.includes("ribbon-exclude");

    if (this.container && (!exclude_product || this.isDesignMode)) {
      this.container.style.display = "block";
      this.updateButtonState(isAvailable);

      let previewNote = this.container.querySelector(".ribbon-preview-note");
      if (this.isDesignMode) {
        if (!previewNote) {
          previewNote = document.createElement("div");
          previewNote.className = "ribbon-preview-note";
          previewNote.textContent = "Preview mode";
          this.container.insertBefore(previewNote, this.container.firstChild);
        }
        previewNote.style.display = "block";
      } else {
        if (previewNote) {
          previewNote.style.display = "none";
        }

        if (!this.isDesignMode) {
          this.container.style.display = shouldShowGiftButtonBasedOnUrl
            ? "block"
            : "none";
        }
      }
    }
  }

  async toggleModal(event) {
    const currentVariantId = this.getCurrentVariantId();
    const variantData = this.variantMetadataMap?.[currentVariantId];
    const modal = document.querySelector("gift-modal");

    // Check availability from modal's current variant
    if (!Boolean(variantData?.available)) {
      event.preventDefault();
      return;
    }
    if (modal) {
      document.body.classList.add("no-scroll");
      // Update modal's variant data before opening
      modal.updateCurrentVariant(currentVariantId);
      modal.toggleVisibility();
    }

    event.stopPropagation();
  }
}

class GiftModal extends HTMLElement {
  constructor() {
    super();
    // Initialize properties and elements
    this.style.display = "none";
    this.appEnvironment = this.getAttribute("data-app-environment");
    this.currentVariant = JSON.parse(this.getAttribute("data-current-variant"));
    this.product = JSON.parse(this.getAttribute("data-product"));
    this.isNoVariantExperience = this.product.variants.length <= 1;
    this.closeModalOnClickOutside = this.closeModalOnClickOutside.bind(this);
    this.handleGiftCheckout = this.handleGiftCheckout.bind(this);
    this.checkoutButton = this.querySelector(".ribbon-checkout-btn");
    this.buttonText = this.checkoutButton.querySelector(".ribbon-button-text");
    this.buttonSpinner = this.checkoutButton.querySelector(
      ".ribbon-button-spinner",
    );
    this.variantMetadataMap =
      parseJsonAttribute(this, "data-variant-gift-metadata") || {};
    if (this.parentNode !== document.body) {
      document.body.appendChild(this);
    }
    this.giftButton = document.querySelector("gift-button");

    this.checkoutButton.addEventListener("click", this.handleGiftCheckout);

    this.currentStep = 1;
    this.totalSteps = 2;
    this.selections = {
      uploaderyUrl: "",
      selectedVariant: this.currentVariant.id.toString(),
      product: this.product.id.toString(),
    };

    this.nameInput = this.querySelector("#recipient-name");
    this.nameErrorNoName = this.querySelector("#name-error-no-name");
    this.nameErrorTooLong = this.querySelector("#name-error-too-long");
    this.emailOrPhoneInput = this.querySelector("#recipient-email-or-phone");
    this.emailOrPhoneErrorEmail = this.querySelector(
      "#email-or-phone-error-email",
    );
    this.emailOrPhoneErrorPhone = this.querySelector(
      "#email-or-phone-error-phone",
    );
    this.emailOrPhoneErrorGeneral = this.querySelector(
      "#email-or-phone-error-general",
    );
    this.countryCodeElement = document.getElementById("country-code");
    this.giftMessage = this.querySelector("#gift-message");
    this.giftMessageErrorTooLong = this.querySelector(
      "#gift-message-error-too-long",
    );
    this.allInputs = this.querySelectorAll(".ribbon-input");
    this.ageConfirmationSection = this.querySelector(
      "#ribbon-age-confirmation",
    );
    this.ageConfirmationCheckbox = this.querySelector(
      "#age-confirmation-checkbox",
    );
    this.ageConfirmationError = this.querySelector("#age-confirmation-error");

    this.nameInput.addEventListener(
      "blur",
      this.handleNameInputBlur.bind(this),
    );
    this.nameInput.addEventListener("input", this.handleNameInput.bind(this));

    this.giftMessage.addEventListener(
      "blur",
      this.handleGiftMessageBlur.bind(this),
    );
    this.giftMessage.addEventListener(
      "input",
      this.handleGiftMessageInput.bind(this),
    );

    this.giftWrappingCheckbox = this.querySelector("#gift-wrap-checkbox");
    this.giftWrappingConfig = JSON.parse(
      this.getAttribute("data-gift-wrapping-config"),
    );
    this.merchantConfig = JSON.parse(this.getAttribute("data-merchant-config"));
    // Check if merchantConfig has callbacks property
    if (this.merchantConfig.callbacks) {
      // Create window.ribbonCallbacks object
      window.ribbonCallbacks = {};

      // Assign functions from merchantConfig.callbacks to window.ribbonCallbacks
      for (const [key, value] of Object.entries(
        this.merchantConfig.callbacks,
      )) {
        window.ribbonCallbacks[key] = new Function("return " + value)();
      }
    }

    const variantData = this.variantMetadataMap[this.currentVariant.id];
    this.variantMetaobject = variantData?.metadata;
  }

  async updateCurrentVariant(newVariantId) {
    const variantData = this.variantMetadataMap[newVariantId];
    this.currentVariant = this.product.variants.find(
      (v) => v.id.toString() === newVariantId.toString(),
    );
    this.variantMetaobject = variantData?.metadata;

    // Update selections
    this.selections.selectedVariant = newVariantId.toString();
  }

  getCookie = (name) => {
    const value = Cookies.get(name);
    if (value === undefined) {
      return null;
    }
    try {
      return JSON.parse(value);
    } catch (e) {
      return value;
    }
  };

  async connectedCallback() {
    // Perform asynchronous initialization here
    this.initializeModal();
  }

  async initializeModal() {
    if (this.parentNode !== document.body) {
      document.body.appendChild(this);
    }

    this.querySelector(".ribbon-close")?.addEventListener("click", () =>
      this.hide(),
    );

    document.addEventListener("click", this.closeModalOnClickOutside);

    this.querySelectorAll(".ribbon-option-btn").forEach((button) => {
      button.addEventListener("click", () => {
        this.goToStep(button.dataset.nextStep);
      });
    });

    this.querySelector(".ribbon-back-button")?.addEventListener("click", () =>
      this.goBack(),
    );

    this.updateProgressBar();

    if (this.merchantConfig?.requireOver21Checkbox) {
      this.initializeAgeConfirmation();
    }
  }

  initializeAgeConfirmation() {
    if (this.ageConfirmationSection) {
      this.ageConfirmationSection.style.display = "flex";
    }

    if (this.ageConfirmationCheckbox) {
      this.ageConfirmationCheckbox.addEventListener("change", () => {
        if (this.ageConfirmationCheckbox.checked) {
          this.ageConfirmationError.style.display = "none";
        }
      });
    }
  }

  // Navigation methods

  goBack() {
    if (this.currentStep > 1) {
      this.resetModal();
    }
  }

  goToStep(step) {
    this.querySelectorAll(".gift-section, .ribbon-initial-section").forEach(
      (section) => {
        section.classList.add("ribbon-hidden");
      },
    );

    const stepElement = this.querySelector(`#step-${step}`);
    if (!stepElement) {
      console.error(`Step ${step} element not found`);
      return;
    }
    stepElement.classList.remove("ribbon-hidden");

    this.currentStep = step;

    this.updateProgressBar();
  }

  // UI update methods

  updateProgressBar() {
    const progressBar = this.querySelector(".ribbon-progress-bar-container");
    if (!progressBar) {
      return;
    }

    const totalWidth = progressBar.offsetWidth;
    const segmentWidth = totalWidth / this.totalSteps;
    const filledWidth = segmentWidth * this.currentStep;

    progressBar.style.setProperty("--filled-width", `${filledWidth}px`);
  }

  closeModalOnClickOutside(event) {
    if (this.style.display !== "none" && !this.contains(event.target)) {
      this.hide();
    }
  }

  // Visibility methods
  toggleVisibility() {
    if (this.style.display === "none") {
      if (this.parentNode !== document.body) {
        document.body.appendChild(this);
      }
      this.show();
    } else {
      this.hide();
    }
  }

  show() {
    setTimeout(() => {
      if (this.style.display === "none") {
        this.style.display = "block";
      }
    }, 100);
    document.body.classList.add("no-scroll");
    this.resetModal();
    logModalOpen(this.currentVariant.id, this.product.id);
  }

  hide() {
    this.style.display = "none";
    document.body.classList.remove("no-scroll");
    logModalClose(this.currentVariant.id, this.product.id);
  }

  resetModal() {
    this.goToStep(1);
    this.selections = {
      selectedVariant: this.currentVariant.id.toString(),
      product: this.product.id.toString(),
    };
    this.resetFormInputs();
    this.resetFormErrors();
  }

  handleNameInputBlur(event) {
    this.resetNameInputErrors();
    const name = event.target.value;
    this.validateName(name);
  }

  validateName(name) {
    if (!name) {
      this.nameErrorNoName.style.display = "block";
      this.nameInput.style.borderColor = "red";
      return false;
    } else if (name.length > 50) {
      this.nameErrorTooLong.style.display = "block";
      this.nameInput.style.borderColor = "red";
      return false;
    }
    return true;
  }

  handleNameInput(event) {
    this.resetNameInputErrors();
  }

  resetNameInputErrors() {
    this.nameErrorNoName.style.display = "none";
    this.nameErrorTooLong.style.display = "none";
    this.nameInput.style.borderColor = "";
  }

  handleGiftMessageBlur(event) {
    this.resetGiftMessageInputErrors();
    const message = event.target.value;
    this.validateGiftMessage(message);
  }

  validateGiftMessage(message) {
    if (message.length > 1000) {
      this.giftMessageErrorTooLong.style.display = "block";
      this.giftMessage.style.borderColor = "red";
      return false;
    }
    return true;
  }

  handleGiftMessageInput(event) {
    this.resetGiftMessageInputErrors();
  }

  resetGiftMessageInputErrors() {
    this.giftMessageErrorTooLong.style.display = "none";
    this.giftMessage.style.borderColor = "";
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.buttonText.classList.add("ribbon-hidden");
      this.buttonSpinner.classList.remove("ribbon-hidden");
      this.checkoutButton.disabled = true;
    } else {
      this.buttonText.classList.remove("ribbon-hidden");
      this.buttonSpinner.classList.add("ribbon-hidden");
      this.checkoutButton.disabled = false;
    }
  }

  resetFormInputs() {
    this.nameInput.value = "";
    this.emailOrPhoneInput.value = "";
    this.giftMessage.value = "";
    this.ageConfirmationCheckbox.checked = false;
    if (this.giftWrappingCheckbox) {
      this.giftWrappingCheckbox.checked = false;
    }
  }

  resetFormErrors() {
    this.emailOrPhoneErrorEmail.style.display = "none";
    this.emailOrPhoneErrorPhone.style.display = "none";
    this.emailOrPhoneErrorGeneral.style.display = "none";
    this.emailOrPhoneInput.style.borderColor = "";
    this.nameInput.style.borderColor = "";
    this.nameErrorNoName.style.display = "none";
    this.nameErrorTooLong.style.display = "none";
    this.giftMessageErrorTooLong.style.display = "none";
    this.giftMessage.style.borderColor = "";
    this.ageConfirmationError.style.display = "none";
  }

  getInternationalPhoneNumber(number, countryCode) {
    const parsedNumber = parsePhoneNumberFromString(number, countryCode);
    return parsedNumber.number;
  }

  // Core functionality
  async handleGiftCheckout() {
    this.setLoading(true);
    let isFormErrors = false;
    this.resetFormErrors();
    // Validate fields
    if (
      this.merchantConfig?.requireOver21Checkbox &&
      !this.ageConfirmationCheckbox.checked
    ) {
      this.ageConfirmationError.style.display = "block";
      isFormErrors = true;
    }

    if (!this.validateName(this.nameInput.value)) {
      isFormErrors = true;
    }

    if (!this.validateGiftMessage(this.giftMessage.value)) {
      isFormErrors = true;
    }

    const contactInput = this.emailOrPhoneInput?.value;
    const contactType = ribbonUtils.isEmailOrPhone(contactInput);

    if (contactType === "email" && !ribbonUtils.validateEmail(contactInput)) {
      this.emailOrPhoneErrorEmail.style.display = "block";
      this.emailOrPhoneInput.style.borderColor = "red";
      isFormErrors = true;
    } else if (
      contactType === "phone" &&
      !ribbonUtils.validatePhoneNumber(
        contactInput,
        this.countryCodeElement.value,
      )
    ) {
      this.emailOrPhoneErrorPhone.style.display = "block";
      this.emailOrPhoneInput.style.borderColor = "red";
      isFormErrors = true;
    } else if (contactType === "unknown") {
      this.emailOrPhoneErrorGeneral.style.display = "block";
      this.emailOrPhoneInput.style.borderColor = "red";
      isFormErrors = true;
    }

    if (isFormErrors) {
      this.setLoading(false);
      return;
    }

    // If get here, no form errors. Let's add to cart.

    const {
      enabled: giftWrappingEnabled,
      variantId: giftWrappingVariantId,
      price: giftWrappingPrice,
    } = this.giftWrappingConfig;

    const uploadery = this.merchantConfig.saveUploaderyUrl
      ? {
        _uploaderyUrl:
          document.querySelector(this.merchantConfig.saveUploaderyUrl)
            ?.value || "",
      }
      : {};
    const properties = {
      deliveryType:
        contactType === "email"
          ? "email_recipient_choice"
          : "sms_recipient_choice",
      ...uploadery,
      recipientEmail:
        contactType === "email" ? this.emailOrPhoneInput.value : "",
      recipientNumber:
        contactType === "phone"
          ? this.getInternationalPhoneNumber(
            contactInput,
            this.countryCodeElement.value,
          )
          : "",
      phoneCountryCode:
        contactType === "phone" ? this.countryCodeElement.value : "",
      giftMessage: this.giftMessage.value,
      recipientName: this.nameInput ? this.nameInput.value : "",
      productId: this.selections.product,
      variantId: this.selections.selectedVariant,
    };

    if (
      this.giftWrappingCheckbox?.checked &&
      giftWrappingEnabled &&
      giftWrappingVariantId &&
      giftWrappingPrice
    ) {
      properties.wrappingVariantId = giftWrappingVariantId;
      properties.wrappingPrice = +giftWrappingPrice;
    }

    // Check if we should use Alpine checkout
    const useAlpineCheckout = this.merchantConfig?.useAlpineCheckout;

    // Add virtual variant to cart
    try {
      if (useAlpineCheckout) {
        await ribbonUtils.ribbonIntegration(properties, useAlpineCheckout);
        this.hide();
      } else {
        await ribbonUtils.ribbonIntegration(properties);
      }
      logVirtualGiftAdded(
        this.selections.selectedVariant,
        this.selections.product,
        properties,
      );
    } catch (error) {
      logVirtualGiftAddFailed(
        this.selections.selectedVariant,
        this.selections.product,
        properties,
      );
      console.error("Error adding virtual variant to cart:", error);
      this.setLoading(false);
      return null;
    }

    this.setLoading(false);
  }
}

customElements.define("gift-modal", GiftModal);
customElements.define("gift-button", GiftButton);
